/**
 * Talk-o 角色与对话管理
 *
 * 功能：
 * 1. Prompt 实验室：创建、编辑、删除、切换 AI Persona 系统提示词
 * 2. 对话洞察：以微信聊天气泡形式回顾历史对话，
 *    并通过 HTML5 <audio> 分别播放用户和 AI 的本地录音。
 */

import { useEffect, useRef, useState } from 'react'

// =============================================================================
// 类型定义
// =============================================================================

interface Persona {
  id: number
  name: string
  system_prompt: string
  is_active: boolean
  voice: string
  created_at: string | null
}

interface ChatLog {
  id: number
  timestamp: string | null
  kid_text: string
  ai_text: string
  audio_file_path: string
  ai_audio_file_path: string | null
}

// =============================================================================
// 工具函数
// =============================================================================

const API_BASE = '/api/admin'

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`)
  }

  if (response.status === 204) {
    return undefined as unknown as T
  }

  return (await response.json()) as T
}

function formatTime(iso: string | null): string {
  if (!iso) return '时间未知'
  try {
    const date = new Date(iso)
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatDateLabel(iso: string | null): string {
  if (!iso) return '今天'
  try {
    const date = new Date(iso)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    if (isToday) return '今天'
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
  } catch {
    return '今天'
  }
}

// =============================================================================
// 子组件
// =============================================================================

const Spinner = ({ className = '' }: { className?: string }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
)

const VoiceOptions = () => (
  <>
    <option value="Fenrir">Fenrir · 低沉男声</option>
    <option value="Aoede">Aoede · 温柔女声</option>
    <option value="Charon">Charon · 浑厚男声</option>
    <option value="Kore">Kore · 清脆女声</option>
    <option value="Puck">Puck · 俏皮活泼</option>
  </>
)

// =============================================================================
// 主组件
// =============================================================================

export default function AdminDashboard() {
  const [personas, setPersonas] = useState<Persona[]>([])
  const [logs, setLogs] = useState<ChatLog[]>([])
  const [form, setForm] = useState({ name: '', systemPrompt: '', voice: 'Fenrir' })
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ name: '', systemPrompt: '', voice: 'Fenrir' })
  const [loadingPersonas, setLoadingPersonas] = useState(false)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const logsTopRef = useRef<HTMLDivElement>(null)

  const startEdit = (persona: Persona) => {
    setEditingId(persona.id)
    setEditForm({ name: persona.name, systemPrompt: persona.system_prompt, voice: persona.voice })
    setError(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
  }

  const loadPersonas = async () => {
    setLoadingPersonas(true)
    try {
      const data = await apiFetch<Persona[]>(`${API_BASE}/personas`)
      setPersonas(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载 Persona 失败')
    } finally {
      setLoadingPersonas(false)
    }
  }

  const loadLogs = async () => {
    setLoadingLogs(true)
    try {
      const data = await apiFetch<ChatLog[]>(`${API_BASE}/logs`)
      setLogs(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载对话日志失败')
    } finally {
      setLoadingLogs(false)
    }
  }

  useEffect(() => {
    loadPersonas()
    loadLogs()
    const timer = setInterval(() => {
      loadLogs()
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (logsTopRef.current) {
      logsTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [logs.length])

  const handleCreatePersona = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = form.name.trim()
    const systemPrompt = form.systemPrompt.trim()

    if (!name || !systemPrompt) {
      setError('请填写角色名称和系统提示词')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await apiFetch(`${API_BASE}/personas`, {
        method: 'POST',
        body: JSON.stringify({ name, system_prompt: systemPrompt, voice: form.voice }),
      })
      setForm({ name: '', systemPrompt: '', voice: 'Fenrir' })
      await loadPersonas()
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建角色失败')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdatePersona = async (e: React.FormEvent, id: number) => {
    e.preventDefault()
    const name = editForm.name.trim()
    const systemPrompt = editForm.systemPrompt.trim()

    if (!name || !systemPrompt) {
      setError('请填写角色名称和系统提示词')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await apiFetch(`${API_BASE}/personas/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name, system_prompt: systemPrompt, voice: editForm.voice }),
      })
      setEditingId(null)
      await loadPersonas()
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新角色失败')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeletePersona = async (id: number, name: string) => {
    if (!window.confirm(`确定要删除角色「${name}」吗？\n删除后，历史对话仍会保留在数据库中。`)) {
      return
    }

    setError(null)
    try {
      await apiFetch(`${API_BASE}/personas/${id}`, { method: 'DELETE' })
      await loadPersonas()
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除角色失败')
    }
  }

  const handleActivate = async (id: number) => {
    setError(null)
    try {
      await apiFetch(`${API_BASE}/personas/${id}/activate`, {
        method: 'PUT',
      })
      await loadPersonas()
    } catch (err) {
      setError(err instanceof Error ? err.message : '切换角色失败')
    }
  }

  const activePersona = personas.find((p) => p.is_active)

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col lg:flex-row">
      {/* 左侧导航栏 - 高级深色边栏 */}
      <aside className="w-full lg:w-64 bg-slate-900 text-white flex flex-col shrink-0 lg:fixed lg:h-screen lg:left-0 lg:top-0 z-20">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-violet-900/50">
              <span className="text-xl">🎙️</span>
            </div>
            <div>
              <h1 className="font-bold text-lg tracking-tight">Talk-o</h1>
              <p className="text-xs text-slate-400">角色与对话管理</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <a
            href="#personas"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-sky-600/20 text-sky-200 border border-sky-500/20 transition-colors"
          >
            <span className="text-lg">🧙</span>
            <span className="font-medium text-sm">Prompt 实验室</span>
          </a>
          <a
            href="#logs"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800/60 hover:text-slate-100 transition-colors"
          >
            <span className="text-lg">💬</span>
            <span className="font-medium text-sm">对话洞察</span>
          </a>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <a
            href="/"
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-sm font-medium text-slate-200 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12l9-9 9 9M12 3v18" />
            </svg>
            返回应用首页
          </a>
        </div>
      </aside>

      {/* 主内容区 */}
      <main className="flex-1 lg:ml-64 min-w-0">
        {/* 顶部状态栏 */}
        <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-slate-200 px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">控制中心</h2>
              <p className="text-sm text-slate-500 mt-0.5">管理 AI 角色并回顾对话历史</p>
            </div>
            {activePersona && (
              <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-100 shadow-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <span className="text-sm text-slate-600">当前生效：</span>
                <span className="text-sm font-bold text-sky-700">{activePersona.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-sky-100 text-sky-600">🎤 {activePersona.voice}</span>
              </div>
            )}
          </div>
        </header>

        {error && (
          <div className="mx-6 mt-6 bg-rose-50 border border-rose-200 rounded-2xl px-5 py-4 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-rose-500 text-lg">⚠️</span>
              <span className="text-rose-700 text-sm font-medium">{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-rose-600 hover:text-rose-800 text-sm font-semibold px-3 py-1 rounded-lg hover:bg-rose-100 transition-colors"
            >
              关闭
            </button>
          </div>
        )}

        <div className="p-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* 左侧：Prompt 实验室 */}
          <section id="personas" className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white shadow-lg shadow-sky-200">
                <span className="text-lg">🧙</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Prompt 实验室</h3>
                <p className="text-xs text-slate-500">创建、编辑角色，设定系统提示词与专属音色</p>
              </div>
            </div>

            {/* 新增角色卡片 */}
            <div className="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
              <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center text-xs">+</span>
                新增角色
              </h4>
              <form onSubmit={handleCreatePersona} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">角色名称</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="例如：糊涂教授、十万个为什么"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">系统提示词</label>
                  <textarea
                    value={form.systemPrompt}
                    onChange={(e) => setForm((prev) => ({ ...prev, systemPrompt: e.target.value }))}
                    placeholder="控制 AI 语气、回答风格和知识范围的提示词..."
                    rows={4}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm resize-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">专属音色</label>
                  <select
                    value={form.voice}
                    onChange={(e) => setForm((prev) => ({ ...prev, voice: e.target.value }))}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm transition-all"
                  >
                    <VoiceOptions />
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-xl text-sm shadow-lg shadow-sky-200 transition-all"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner className="w-4 h-4" /> 创建中...
                    </span>
                  ) : (
                    '创建角色'
                  )}
                </button>
              </form>
            </div>

            {/* 角色列表 */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-800">角色列表</h4>
                {loadingPersonas && (
                  <span className="text-xs text-slate-400 flex items-center gap-1.5">
                    <Spinner className="w-3 h-3" /> 刷新中...
                  </span>
                )}
              </div>

              {personas.length === 0 && !loadingPersonas && (
                <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200">
                  <div className="text-4xl mb-3">🎭</div>
                  <div className="text-slate-500 text-sm font-medium">暂无角色</div>
                  <div className="text-slate-400 text-xs mt-1">在上方创建一个新角色吧</div>
                </div>
              )}

              {personas.map((persona) => (
                <div
                  key={persona.id}
                  className={`relative bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border transition-all ${persona.is_active
                    ? 'border-violet-300 ring-1 ring-violet-200'
                    : 'border-slate-100 hover:border-slate-200'
                    }`}
                >
                  {persona.is_active && (
                    <div className="absolute -top-px left-8 right-8 h-0.5 bg-gradient-to-r from-sky-500 via-indigo-500 to-cyan-500 rounded-full" />
                  )}

                  {editingId === persona.id ? (
                    <form onSubmit={(e) => handleUpdatePersona(e, persona.id)} className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">角色名称</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">系统提示词</label>
                        <textarea
                          value={editForm.systemPrompt}
                          onChange={(e) => setEditForm((prev) => ({ ...prev, systemPrompt: e.target.value }))}
                          rows={4}
                          className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm resize-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">专属音色</label>
                        <select
                          value={editForm.voice}
                          onChange={(e) => setEditForm((prev) => ({ ...prev, voice: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 text-sm transition-all"
                        >
                          <VoiceOptions />
                        </select>
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white font-semibold py-2 rounded-xl text-sm transition-colors"
                        >
                          {submitting ? '保存中...' : '保存修改'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={submitting}
                          className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 rounded-xl text-sm transition-colors"
                        >
                          取消
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-2 mb-3">
                            <h5 className="font-bold text-slate-900">{persona.name}</h5>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                              🎤 {persona.voice}
                            </span>
                            {persona.is_active && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                当前生效
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-xl p-3.5 border border-slate-100 select-text">
                            {persona.system_prompt}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          onClick={() => startEdit(persona)}
                          className="px-3.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-600 hover:bg-violet-50 text-xs font-semibold transition-all"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDeletePersona(persona.id, persona.name)}
                          className="px-3.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-600 hover:bg-rose-50 text-xs font-semibold transition-all"
                        >
                          删除
                        </button>
                        <button
                          onClick={() => handleActivate(persona.id)}
                          disabled={persona.is_active}
                          className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${persona.is_active
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-slate-900 text-white hover:bg-slate-800 shadow-md shadow-slate-200'
                            }`}
                        >
                          {persona.is_active ? '已生效' : '设为当前生效'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* 右侧：对话洞察 */}
          <section id="logs" className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-lg shadow-emerald-200">
                <span className="text-lg">💬</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">对话洞察</h3>
                <p className="text-xs text-slate-500">回顾用户与 AI 的每一次交流</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">近期对话</span>
                {loadingLogs && logs.length > 0 && (
                  <span className="text-xs text-slate-400 flex items-center gap-1.5">
                    <Spinner className="w-3 h-3" /> 刷新中...
                  </span>
                )}
              </div>

              <div className="p-5 space-y-8 max-h-[calc(100vh-240px)] overflow-y-auto">
                <div ref={logsTopRef} />

                {loadingLogs && logs.length === 0 && (
                  <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
                    <Spinner className="w-5 h-5 mr-2" /> 加载对话记录中...
                  </div>
                )}

                {logs.length === 0 && !loadingLogs && (
                  <div className="text-center py-16">
                    <div className="text-5xl mb-4">📚</div>
                    <div className="text-slate-600 font-medium text-sm">暂无对话记录</div>
                    <div className="text-slate-400 text-xs mt-1">开始一次对话后，这里会显示交流记录</div>
                  </div>
                )}

                {logs.map((log) => (
                  <div key={log.id} className="relative">
                    {/* 日期分割线 */}
                    <div className="absolute -top-4 left-0 right-0 flex items-center justify-center">
                      <div className="bg-slate-100 text-slate-500 text-[10px] font-semibold px-3 py-1 rounded-full">
                        {formatDateLabel(log.timestamp)} · {formatTime(log.timestamp)}
                      </div>
                    </div>

                    {/* 用户气泡 */}
                    <div className="flex justify-start mb-6 pt-3">
                      <div className="max-w-[90%]">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-[10px] font-bold">
                            我
                          </div>
                          <span className="text-[11px] font-semibold text-slate-500">用户</span>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-sky-50 text-slate-700 px-4 py-3 rounded-2xl rounded-tl-sm text-sm leading-relaxed shadow-sm border border-blue-100 inline-block">
                          {log.kid_text?.trim() || (
                            <span className="italic text-slate-400">(无文字内容)</span>
                          )}
                        </div>
                        {log.audio_file_path && (
                          <div className="mt-2">
                            <audio
                              controls
                              src={`${API_BASE}/logs/${log.id}/audio`}
                              className="h-9 w-[220px] sm:w-[260px] rounded-full"
                              preload="none"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* AI 气泡 */}
                    <div className="flex justify-end mb-6">
                      <div className="max-w-[90%] flex flex-col items-end">
                        <div className="flex items-center gap-2 mb-1.5 flex-row-reverse">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold">
                            AI
                          </div>
                          <span className="text-[11px] font-semibold text-slate-500">{activePersona?.name || 'AI'}</span>
                        </div>
                        <div className="bg-gradient-to-br from-sky-50 to-indigo-50 text-slate-700 px-4 py-3 rounded-2xl rounded-tr-sm text-sm leading-relaxed shadow-sm border border-sky-100 inline-block">
                          {log.ai_text?.trim() || (
                            <span className="italic text-slate-400">(无文字内容)</span>
                          )}
                        </div>
                        {log.ai_audio_file_path && (
                          <div className="mt-2">
                            <audio
                              controls
                              src={`${API_BASE}/logs/${log.id}/ai_audio`}
                              className="h-9 w-[220px] sm:w-[260px] rounded-full"
                              preload="none"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}
